import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import { JOURNEY_PHOTOS } from "../photoManifest";
import { JOURNEY_CAPTIONS, JOURNEY_OUTRO } from "../MothersDayData";

const SLIDE_MS = 4500;

const JourneyAct = ({ onNext }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = JOURNEY_PHOTOS.length;
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(() => {
      setIdx((i) => Math.min(i + 1, total));
    }, SLIDE_MS);
    return () => clearTimeout(timer.current);
  }, [idx, paused, total]);

  const finished = idx >= total;
  const photo = idx < total ? JOURNEY_PHOTOS[idx] : null;
  const caption = idx < total ? JOURNEY_CAPTIONS[idx] || "" : "";

  // Ken Burns: pick a deterministic transform per slide
  const kb = (i) => {
    const variants = [
      { initial: { scale: 1.04, x: -10, y: -8 },  animate: { scale: 1.16, x: 12,  y: 10 } },
      { initial: { scale: 1.18, x: 12,  y: 10 },  animate: { scale: 1.04, x: -12, y: -10 } },
      { initial: { scale: 1.06, x: 10,  y: -10 }, animate: { scale: 1.18, x: -8,  y: 12 } },
      { initial: { scale: 1.18, x: -8,  y: 8 },   animate: { scale: 1.06, x: 8,   y: -10 } },
    ];
    return variants[i % variants.length];
  };

  return (
    <motion.div
      className="md-act md-act-journey"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="md-journey-stage" onClick={() => setPaused((p) => !p)}>
        <AnimatePresence mode="wait">
          {!finished && photo && (
            <motion.div
              key={idx}
              className="md-journey-slide"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9 }}
            >
              <motion.div
                className="md-journey-img"
                style={{ backgroundImage: `url(${photo})` }}
                initial={kb(idx).initial}
                animate={kb(idx).animate}
                transition={{ duration: SLIDE_MS / 1000 + 0.6, ease: "linear" }}
              />
              <div className="md-journey-overlay" />
              <motion.div
                className="md-journey-caption"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <span className="md-journey-meta">{idx + 1} / {total}</span>
                <p>{caption}</p>
              </motion.div>
            </motion.div>
          )}

          {finished && (
            <motion.div
              key="outro"
              className="md-journey-slide md-journey-outro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <h2>{JOURNEY_OUTRO}</h2>
              <button className="md-btn-soft md-cta" onClick={onNext}>
                Now help Aven build something →
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="md-journey-controls">
        <div className="md-journey-progress">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className={`md-journey-bar ${i < idx ? "md-on" : ""} ${i === idx ? "md-now" : ""}`} />
          ))}
        </div>
        <div className="md-journey-actions">
          <button className="md-btn-link" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>← prev</button>
          <button className="md-btn-link" onClick={() => setPaused((p) => !p)}>{paused ? "▶ play" : "❚❚ pause"}</button>
          <button className="md-btn-link" onClick={() => setIdx((i) => Math.min(total, i + 1))}>next →</button>
          <button className="md-btn-link" onClick={onNext} style={{ marginLeft: "auto" }}>skip ⤳</button>
        </div>
      </div>
    </motion.div>
  );
};

export default JourneyAct;
